/**
 * Disciplines — de drie groepen waarin het team is ingedeeld (Dagstart, capaciteit).
 *
 * Waarom een expliciete namenlijst en geen afleiding uit `Functie`? Omdat Functie
 * een multi-select is die de praktijk niet volgt: Ton en Pepijn zijn projectleider
 * én cameraman, Michel is regisseur én editor, en Misja staat als 'Archief'. Elke
 * regel die je op die velden loslaat zet minstens vier mensen in de verkeerde groep.
 * De Airtable-views waar het team mee werkt zijn met de hand samengesteld; deze lijst
 * is de spiegel daarvan.
 *
 * Nieuwe collega niet in de lijst? Dan valt hij terug op zijn Functie, en anders op
 * Productie — dezelfde plek waar 'overig' in de views ook staat. Voor een blijvende
 * oplossing hoort er een 'Discipline'-veld op de Personen-tabel te komen; dan kan
 * deze lijst weg.
 */

export const DISCIPLINES = [
  { key: 'animatie', label: 'Animatie & Marketing' },
  { key: 'camera_edit', label: 'Camera & Edit' },
  { key: 'productie', label: 'Productie & Regie' }, // wiki Dagstart 26 sep (was: Productie)
];

/** Handmatige indeling, gespiegeld aan de Airtable-weekoverzichten (aug 2026). */
const VASTE_INDELING = {
  animatie: ['Eva', 'Isa', 'Isabelle', 'Julia', 'Mauri', 'Noelle', 'Niels', 'Mila', 'Jade'],
  camera_edit: ['Bram', 'Jur', 'Leon', 'Michel', 'Misja', 'Pepijn', 'Robin', 'Thijmen', 'Ton', 'Luuk', 'Sten'],
  productie: ['Arthur', 'Boy', 'Edwin', 'Evie', 'Henk', 'Joanne', 'Joris', 'Lars', 'Naomi', 'Raijmond', 'Romy', 'Tess', 'Vincent', 'Enya'],
};

/** Voornamen (kleine letters) van de vaste interne medewerkers — o.a. om externe crew in draaidagtitels te herkennen. */
export const INTERNE_VOORNAMEN = Object.values(VASTE_INDELING).flat().map((n) => n.toLowerCase());

/** Terugval voor wie nog niet in de lijst staat: grofweg op functie. */
const FUNCTIE_FALLBACK = [
  ['animatie', ['animator', 'illustrator', 'art director', 'social media', 'marketing', 'content creator', 'copywriter']],
  ['camera_edit', ['editor', 'cameraman', 'camera operator', 'dop', 'cinematograaf', 'gaffer', 'geluidstechnicus', 'colorist', 'fotograaf']],
];

const voornaamVan = (naam) => String(naam || '').trim().split(/\s+/)[0];

/**
 * Functie kan string of array zijn — zie de multipleSelects-valkuil in CLAUDE.md.
 */
export const functieAlsTekst = (persoon) => {
  const ruw = persoon?.function ?? persoon?.functie ?? persoon?.Functie ?? '';
  return (Array.isArray(ruw) ? ruw.join(', ') : String(ruw)).toLowerCase();
};

/** Geeft de discipline-key van een persoon (naam of persoonsobject). */
export function disciplineVan(persoonOfNaam) {
  const persoon = typeof persoonOfNaam === 'string' ? { name: persoonOfNaam } : (persoonOfNaam || {});
  const voornaam = voornaamVan(persoon.name || persoon.naam || persoon.firstName || '');

  for (const [key, namen] of Object.entries(VASTE_INDELING)) {
    if (namen.some((n) => n.toLowerCase() === voornaam.toLowerCase())) return key;
  }

  const functie = functieAlsTekst(persoon);
  for (const [key, sleutelwoorden] of FUNCTIE_FALLBACK) {
    if (sleutelwoorden.some((s) => functie.includes(s))) return key;
  }
  return 'productie';
}

export const disciplineLabel = (key) => DISCIPLINES.find((d) => d.key === key)?.label || key;
